import { useCallback } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { AutoComplete } from 'primereact/autocomplete';
import { Dropdown } from 'primereact/dropdown';
import { Fieldset } from 'primereact/fieldset';
import {
  InputNumber,
  InputNumberValueChangeEvent,
} from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';
import { FormField } from '@/components/common/form-field';
import { MassEditWizard } from '@/components/common/mass-edit-wizard';
import { WizardConfig } from '@/components/common/mass-edit-wizard/config';
import { MIN_SEARCH_LENGTH, SELECTION_LIMIT } from '@/constants';
import { useStringMap } from '@/hooks/useStringMap';
import {
  billStatusOptions,
  shipmentCategoryOptions,
  shippingMethodOptions,
  usStateOptions,
} from '@/pages/shipping-details/utils/constants';
import { ShipmentStatus } from '@/pages/shipping/utils/enums';
import {
  massUpdateSchema,
  type MassUpdateSchema,
} from '@/pages/shipping/utils/schema';
import {
  useShipmentAccountSearch,
  useShipmentEquipmentSearch,
  useShipmentSiteSearch,
  useUpdateShipment,
} from '@/services/shipping/hooks';
import type { ShipmentListItem } from '@/services/shipping/types';

type ShipmentFormProps = {
  shipmentIds: string[];
  rows: ShipmentListItem[];
  config: WizardConfig;
  onSuccess?: () => void;
  onCancel: () => void;
  themeMode?: 'light' | 'dark';
};

export const ShipmentForm: React.FC<ShipmentFormProps> = ({
  rows,
  config,
  onSuccess,
  onCancel,
  themeMode,
}) => {
  const { t } = useTranslation();
  const { handleSubmit, control, formState, setValue, watch, trigger } =
    useForm<MassUpdateSchema>({
      resolver: zodResolver(massUpdateSchema),
      defaultValues: {},
    });

  const isSubmitting = false;
  const isShipped = rows.some(
    (row) => row.shipmentStatus === ShipmentStatus.SHIPPED,
  );

  const { mutateAsync: updateShipment } = useUpdateShipment();

  const {
    filteredResults: filteredEquipments,
    search: searchEquipment,
    setFilteredResults: setFilteredEquipments,
  } = useShipmentEquipmentSearch();

  const {
    filteredResults: filteredShipFromSites,
    search: searchShipFromSites,
    setFilteredResults: setFilteredShipFromSites,
  } = useShipmentSiteSearch();

  const {
    filteredResults: filteredShipToSites,
    search: searchShipToSites,
    setFilteredResults: setFilteredShipToSites,
  } = useShipmentAccountSearch();

  const {
    filteredResults: filteredAccounts,
    search: searchAccount,
    setFilteredResults: setFilteredAccounts,
  } = useShipmentAccountSearch();

  const handleBulkEditSubmit = useCallback(
    async (
      formValues: MassUpdateSchema,
      shipmentRows: ShipmentListItem[],
    ): Promise<Array<{ success: boolean; message?: string | null }>> => {
      const updates = Object.entries(formValues).reduce(
        (acc, [key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            acc[key] = value;
          }
          return acc;
        },
        {} as Record<string, unknown>,
      );

      if (updates.owner && typeof updates.owner === 'object') {
        updates.ownerId = (updates.owner as { id: string }).id;
        delete updates.owner;
      }
      if (updates.packedBy && typeof updates.packedBy === 'object') {
        updates.packedById = (updates.packedBy as { id: string }).id;
        delete updates.packedBy;
      }
      if (updates.shipFromSite && typeof updates.shipFromSite === 'object') {
        updates.shipFromSiteId = (updates.shipFromSite as { id: string }).id;
        delete updates.shipFromSite;
      }
      if (updates.shipTo && typeof updates.shipTo === 'object') {
        updates.shipToId = (updates.shipTo as { id: string }).id;
        delete updates.shipTo;
      }

      return updateShipment(shipmentRows, updates);
    },
    [updateShipment],
  );

  const shipmentStatus = useStringMap('shipment_status');

  const formContent = (
    <div className="content-panel grid grid-cols-3 gap-4">
      <Fieldset
        legend={t('shipping.shipmentInformation')}
        className="col-span-2"
        pt={{ content: { className: 'grid grid-cols-2 gap-4' } }}
      >
        <FormField
          label={t('account.account')}
          name="owner"
          control={control}
          setValue={setValue}
          component={AutoComplete}
          props={{
            field: 'name',
            suggestions: filteredAccounts,
            minLength: MIN_SEARCH_LENGTH,
            selectionLimit: SELECTION_LIMIT,
            completeMethod: searchAccount,
            onDropdownClick: () => setFilteredAccounts(undefined),
            dropdown: true,
            value: watch('owner') ?? '',
            autoFocus: true,
            disabled: true,
          }}
        />

        <FormField
          label={t('shipping.needByDate')}
          name="needByDate"
          control={control}
          component={Calendar}
          props={{
            showIcon: true,
            disabled: isShipped,
          }}
        />

        <FormField
          label={t('shipping.originalNeedByDate')}
          name="originalNeedByDate"
          control={control}
          component={Calendar}
          props={{
            showIcon: true,
            disabled: true,
          }}
        />

        <FormField
          label={t('shipping.shipmentStatus')}
          name="shipmentStatus"
          control={control}
          component={Dropdown}
          props={{
            options: shipmentStatus.options,
            disabled: isShipped,
          }}
        />
        <FormField
          label={t('shipping.shipFromSite')}
          name="shipFromSite"
          control={control}
          setValue={setValue}
          component={AutoComplete}
          props={{
            field: 'name',
            suggestions: filteredShipFromSites,
            minLength: MIN_SEARCH_LENGTH,
            selectionLimit: SELECTION_LIMIT,
            completeMethod: searchShipFromSites,
            onDropdownClick: () => setFilteredShipFromSites(undefined),
            dropdown: true,
            value: watch('shipFromSite') ?? '',
            onChange: (e: any) => setValue('shipFromSite', e.value),
            disabled: true,
          }}
        />
        <FormField
          label={t('common.date')}
          name="date"
          control={control}
          component={Calendar}
          props={{
            showIcon: true,
            disabled: isShipped,
          }}
        />
      </Fieldset>

      <Fieldset
        legend={t('shipping.contact')}
        pt={{ content: { className: 'flex flex-col gap-4' } }}
      >
        <FormField
          label={t('shipping.requestedBy')}
          name="requestedBy"
          control={control}
          component={InputText}
          props={{
            disabled: true,
          }}
        />
        <FormField
          label={t('shipping.contactPhone')}
          name="phone"
          control={control}
          component={InputText}
          props={{
            disabled: true,
          }}
        />
        <FormField
          label={t('shipping.contactEmail')}
          name="email"
          control={control}
          component={InputText}
          props={{
            disabled: true,
          }}
        />
      </Fieldset>

      <Fieldset
        legend={t('shipping.shipmentDestination')}
        pt={{ content: { className: 'flex flex-col gap-4' } }}
      >
        <FormField
          label={t('shipping.shipTo')}
          name="shipTo"
          control={control}
          setValue={setValue}
          component={AutoComplete}
          props={{
            field: 'name',
            suggestions: filteredShipToSites,
            minLength: MIN_SEARCH_LENGTH,
            selectionLimit: SELECTION_LIMIT,
            completeMethod: searchShipToSites,
            onDropdownClick: () => setFilteredShipToSites(undefined),
            dropdown: true,
            value: watch('shipTo') ?? '',
            disabled: isShipped,
          }}
        />
        <FormField
          label={t('shipping.shipName')}
          name="shipName"
          control={control}
          component={InputText}
          props={{
            disabled: isShipped,
          }}
        />
        <FormField
          label={t('shipping.shipAddress')}
          name="shipAddress"
          control={control}
          component={InputText}
          props={{
            disabled: isShipped,
          }}
        />
        <FormField
          label={t('shipping.shipAddress2')}
          name="shipAddress2"
          control={control}
          component={InputText}
          props={{
            disabled: isShipped,
          }}
        />
        <FormField
          label={t('shipping.shipAddress3')}
          name="shipAddress3"
          control={control}
          component={InputText}
          props={{
            disabled: isShipped,
          }}
        />
        <FormField
          label={t('shipping.shipCity')}
          name="shipCity"
          control={control}
          component={InputText}
          props={{
            disabled: isShipped,
          }}
        />
        <FormField
          label={t('shipping.shipState')}
          name="shipState"
          control={control}
          component={Dropdown}
          props={{
            options: usStateOptions,
            editable: true,
            filter: true,
            disabled: isShipped,
          }}
        />
        <FormField
          label={t('shipping.shipZip')}
          name="shipZip"
          control={control}
          component={InputText}
          props={{
            disabled: isShipped,
          }}
        />
      </Fieldset>

      <div className="flex flex-col gap-4">
        <Fieldset
          legend={t('shipping.shippingDetails')}
          pt={{ content: { className: 'flex flex-col gap-4' } }}
        >
          <div className="label-input-group">
            <label htmlFor="amount" id="amount-label">
              Amount
            </label>
            <Controller
              name="amount"
              control={control}
              render={({ field, fieldState }) => (
                <InputNumber
                  id={field.name}
                  inputId={field.name}
                  aria-labelledby="amount-label"
                  name={field.name}
                  onBlur={field.onBlur}
                  inputRef={field.ref}
                  value={field.value ?? null}
                  disabled={isShipped}
                  onValueChange={(e: InputNumberValueChangeEvent) =>
                    field.onChange(e.value)
                  }
                  mode="currency"
                  currency="USD"
                  locale="en-US"
                  minFractionDigits={2}
                  maxFractionDigits={2}
                  showButtons={false}
                  className={fieldState.invalid ? 'p-invalid' : ''}
                />
              )}
            />
          </div>

          <FormField
            label={t('shipping.tankIncubator')}
            name="tank"
            control={control}
            component={InputText}
            props={{
              disabled: isShipped,
            }}
          />

          <FormField
            label={t('shipping.shippingMethod')}
            name="shippingMethod"
            control={control}
            component={Dropdown}
            props={{
              options: shippingMethodOptions,
              disabled: isShipped,
            }}
          />

          <FormField
            label={t('shipping.trackingNumber')}
            name="trackingNumber"
            control={control}
            component={InputText}
            props={{
              disabled: isShipped,
            }}
          />
        </Fieldset>

        <Fieldset pt={{ content: { className: 'flex flex-col gap-4' } }}>
          <FormField
            label={t('shipping.callTag')}
            name="callTag"
            control={control}
            component={InputText}
            props={{
              disabled: isShipped,
            }}
          />
          <FormField
            label={t('shipping.shipmentCategory')}
            name="shipmentCategory"
            control={control}
            component={Dropdown}
            props={{
              options: shipmentCategoryOptions,
              disabled: isShipped,
            }}
          />
        </Fieldset>

        <Fieldset pt={{ content: { className: 'flex flex-col gap-4' } }}>
          <FormField
            label={t('shipping.packedBy')}
            name="packedBy"
            control={control}
            setValue={setValue}
            component={AutoComplete}
            props={{
              field: 'name',
              suggestions: filteredEquipments,
              minLength: MIN_SEARCH_LENGTH,
              selectionLimit: SELECTION_LIMIT,
              completeMethod: searchEquipment,
              onDropdownClick: () => setFilteredEquipments(undefined),
              dropdown: true,
              value: watch('packedBy') ?? '',
              onChange: (e: any) => setValue('packedBy', e.value),
              disabled: isShipped,
            }}
          />
        </Fieldset>
      </div>

      <Fieldset
        legend={t('billing.billing')}
        pt={{ content: { className: 'flex flex-col gap-4' } }}
      >
        <FormField
          label={t('billing.billTo')}
          name="billTo"
          control={control}
          component={InputText}
          props={{
            disabled: true,
          }}
        />
        <FormField
          label={t('billing.billStatus')}
          name="billStatus"
          control={control}
          component={Dropdown}
          props={{
            options: billStatusOptions,
            disabled: true,
          }}
        />
      </Fieldset>

      <Fieldset pt={{ content: { className: 'flex flex-col gap-4' } }}>
        <div className="label-input-group comment">
          <label htmlFor="comment" id="comment-label">
            {t('common.comment')}
          </label>
          <Controller
            name="comment"
            control={control}
            render={({ field }) => (
              <InputTextarea
                id="comment"
                aria-labelledby="comment-label"
                {...field}
                value={field?.value ?? ''}
                disabled={isShipped}
              />
            )}
          />
        </div>
      </Fieldset>
    </div>
  );

  return (
    <MassEditWizard
      rows={rows}
      getFormValues={() => watch()}
      validateForm={trigger}
      config={config}
      onSubmit={handleBulkEditSubmit}
      onSuccess={onSuccess}
      onCancel={onCancel}
      title={t('shipping.bulkEditShipments')}
      step1Header={t('shipping.editShipmentFields')}
      step2Header={t('shipping.reviewChanges')}
      successMessage={t('shipping.successfullyUpdatedShipments')}
      isSubmitting={isSubmitting}
      themeMode={themeMode}
    >
      {formContent}
    </MassEditWizard>
  );
};
